/**
 * Centralized Authentication Service
 *
 * Manages Google OAuth authentication state across all admin pages.
 * Provides:
 * - Token existence check
 * - Token validation against Google Drive API
 * - Token refresh when expired
 * - Reactive auth state via events and direct querying
 * - Login / logout flows
 */

const AUTH_CHECK_URL = 'https://www.googleapis.com/drive/v3/files?pageSize=1&fields=files(id)';

/**
 * Get the stored access token
 */
export function getAccessToken() {
  return localStorage.getItem('google_drive_access_token');
}

/**
 * Get the stored refresh token
 */
function getRefreshToken() {
  return localStorage.getItem('google_drive_refresh_token') || getAccessToken();
}

/**
 * Quick synchronous check if user is authenticated based on localStorage (shared across tabs)
 * Use this for initial UI rendering before async validation
 *
 * The access token is stored in localStorage which persists across browser tabs.
 * The async validation (validateToken) will verify the token is actually valid
 * and attempt refresh if needed. This sync check only verifies a token exists.
 */
export function isAuthenticatedSync() {
  return !!getAccessToken();
}

/**
 * Validate current access token by making a lightweight call to Google Drive API.
 * Returns { valid: boolean, token: string|null }
 */
export async function validateToken() {
  const token = getAccessToken();
  if (!token) {
    return { valid: false, token: null };
  }

  try {
    const response = await fetch(AUTH_CHECK_URL, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (response.ok) {
      return { valid: true, token };
    }

    // Token is expired or invalid — try to refresh
    if (response.status === 401) {
      const refreshed = await refreshAccessToken();
      return { valid: !!refreshed, token: refreshed };
    }

    return { valid: false, token: null };
  } catch (error) {
    console.error('[AuthService] Token validation error:', error);
    return { valid: false, token: null };
  }
}

/**
 * Refresh the access token using the refresh token via backend API
 */
export async function refreshAccessToken() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    console.warn('[AuthService] No refresh token available');
    return null;
  }

  try {
    const response = await fetch('/api/admin-token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (response.ok) {
      const data = await response.json();
      const newToken = data.access_token;
      localStorage.setItem('google_drive_access_token', newToken);
      return newToken;
    }

    // Refresh failed — clear all tokens
    console.error('[AuthService] Token refresh failed:', response.status, await response.text());
    clearAuth();
    return null;
  } catch (error) {
    console.error('[AuthService] Token refresh error:', error);
    return null;
  }
}

/**
 * Perform full authentication check:
 * 1. Quick sync check
 * 2. Async token validation (calls Google Drive API)
 * 3. Attempt refresh if needed
 *
 * Returns { authenticated: boolean, token: string|null, reason: string|null }
 */
export async function checkAuth() {
  // Step 1: Quick synchronous check
  if (!isAuthenticatedSync()) {
    return { authenticated: false, token: null, reason: 'no_session' };
  }

  // Step 2: Validate the token asynchronously
  const { valid, token } = await validateToken();
  if (valid && token) {
    return { authenticated: true, token, reason: null };
  }

  return { authenticated: false, token: null, reason: 'token_invalid' };
}

/**
 * Clear all authentication data
 */
export function clearAuth() {
  sessionStorage.removeItem('admin_authenticated');
  sessionStorage.removeItem('admin_email');
  localStorage.removeItem('google_drive_access_token');
}

/**
 * Initiate Google OAuth login
 */
export function initiateOAuth() {
  window.location.href = '/api/admin-oauth-init';
}

/**
 * Complete logout: clear auth data and redirect
 */
export function logout() {
  clearAuth();
  window.location.href = '/';
}

const AuthService = {
  getAccessToken,
  isAuthenticatedSync,
  validateToken,
  refreshAccessToken,
  checkAuth,
  clearAuth,
  initiateOAuth,
  logout,
};

export default AuthService;